import { ddLogger } from '@b-stock/fe-core/bstock-react'
import type {
  PreferencesDto,
  MarketsDto,
  FavoriteMarketDto,
  FavoriteMarketCreateDto,
  FavoriteMarketUpdateDto,
  FavoriteMarketsDto,
} from '@b-stock/notification-api-client'
import { ChannelSettingsChannelEnum as Channel } from '@b-stock/notification-api-client'

import appConfig from '@config/config'
import { patch, get, post, deleteReq } from '@helpers/xhr'

const { api: apiConfig } = appConfig

/** Parent group label from notification service; matches TransactionEmailPreferences filter */
const TRANSACTIONAL_PARENT_GROUP_LABEL = 'Transactional Updates'

let emailNotificationType = apiConfig.notifications.marketingTypeId // to be populated only once and then reused everytime

/** contact notification svc to update marketing preference(s) without care of response or failure
 * Authorization header is auto set by CookieManager.getCookie(Cookies.ACCESS_TOKEN)
 */

interface UpdateFavoritePayload extends FavoriteMarketUpdateDto {
  _id: string
}

export default {
  updateMarketingPreference: async (
    acceptMarketingSolicitation: boolean,
    { personId }: { personId: string }
  ) => {
    const baseUrl = apiConfig.notifications.preferences
    const marketingTypeId = await getEmailNotificationTypeId()
    if (marketingTypeId) {
      const url = `${baseUrl}/${personId}/type/${marketingTypeId}`
      const data = [
        {
          type: 'EMAIL',
          active: acceptMarketingSolicitation,
        },
      ]
      try {
        const result = await patch<{ data?: unknown; status?: number }>(
          url,
          data
        )
        ddLogger.info(
          `Marketing preference updated, personId: ${personId}, response: ${JSON.stringify(
            result.data
          )}`
        )
      } catch (err) {
        ddLogger.error(
          `Marketing preference update failed, personId: ${personId}, error: ${JSON.stringify(err)}`
        )
      }
    } else {
      ddLogger.error(
        `No marketing type id found, personId: ${personId}-${marketingTypeId}. Emails has not been sent`
      )
    }
  },

  updateNotificationPreference: async (
    personId: string,
    typeId: string,
    channel: Channel,
    active: boolean
  ): Promise<void> => {
    const baseUrl = apiConfig.notifications.preferences
    const url = `${baseUrl}/${personId}/type/${typeId}`

    await patch(url, [{ type: channel, active }])
  },

  /**
   * Sets SMS channel for all supported types under "Transactional Updates" (buyer onboarding SMS opt-in).
   */
  setTransactionalSmsFromOnboarding: async (
    personId: string,
    active: boolean
  ): Promise<void> => {
    try {
      const { data: preferences } = await get<PreferencesDto[]>(
        `${apiConfig.notifications.preferences}/:personId`,
        null,
        { entities: { personId } }
      )

      for (const pref of preferences) {
        if (pref.parentGroup?.label !== TRANSACTIONAL_PARENT_GROUP_LABEL) {
          continue
        }
        for (const typePref of pref.group.types ?? []) {
          const smsChannel = typePref.channelSettings.find(
            (c) => c.channel === Channel.Sms
          )
          if (!smsChannel?.supported) {
            continue
          }
          const baseUrl = apiConfig.notifications.preferences
          const url = `${baseUrl}/${personId}/type/${typePref.id}`
          await patch(url, [{ type: Channel.Sms, active }])
        }
      }

      ddLogger.info('Transactional SMS preferences set from onboarding', {
        target: { personId, active },
      })
    } catch (err: unknown) {
      ddLogger.warn(
        `Transactional SMS preference update failed, personId: ${personId}`,
        {},
        err instanceof Error ? err : new Error(String(err))
      )
    }
  },

  fetchNotificationPreferences: async (personId: string) => {
    const { data } = await get<PreferencesDto[]>(
      `${apiConfig.notifications.preferences}/:personId`,
      null,
      { entities: { personId } }
    )

    return data
  },

  getMarkets: async () => {
    const { data } = await get<MarketsDto>(apiConfig.notifications.markets)

    return data
  },

  getFavorites: async (personId: string) => {
    const { data } = await get<FavoriteMarketsDto>(
      `${apiConfig.notifications.favoriteMarkets}?personId=${encodeURIComponent(personId)}`,
      null,
      { entities: { personId } }
    )
    return data
  },

  createFavorite: async (payload: FavoriteMarketCreateDto) => {
    const { data } = await post<FavoriteMarketDto>(
      apiConfig.notifications.favoriteMarkets,
      payload
    )
    return data
  },

  updateFavorite: async (payload: UpdateFavoritePayload) => {
    const { _id, listingEmailsEnabled } = payload
    const { data } = await patch<FavoriteMarketUpdateDto>(
      `${apiConfig.notifications.favoriteMarkets}/${encodeURIComponent(_id)}`,
      { listingEmailsEnabled }
    )

    return data
  },

  deleteFavorite: async (favoriteId: string) => {
    await deleteReq(
      `${apiConfig.notifications.favoriteMarkets}/${encodeURIComponent(favoriteId)}`
    )
  },
}

// used when process.env.NOTIFICATION_TYPE_ID not defined. Helpful local developments and new servers
async function getEmailNotificationTypeId() {
  try {
    // may have been loaded by environment variable
    if (emailNotificationType) {
      return emailNotificationType
    }

    const emailTypeName = apiConfig.notifications.marketingEmailTypeName

    const baseUrl = apiConfig.notifications.types
    const url = `${baseUrl}?_type=${emailTypeName}`
    const results = await get<{ types?: Array<{ _id?: string }> }>(url)
    // In local development we may not have the type in the database
    const type = results.data.types?.[0]?._id
    if (type) {
      // store result to always be used again
      emailNotificationType = type
      return emailNotificationType
    }
    return null
  } catch (error) {
    ddLogger.error('Error fetching email notification type id', { error })
    return null
  }
}
